import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/EditingRoom/TopBar';
import PreflightQA from '../components/EditingRoom/PreflightQA';
import FareCard from '../components/EditingRoom/FareCard';
import InProgress from '../components/EditingRoom/InProgress';
import ReviewRoom from '../components/EditingRoom/ReviewRoom';
import DeadEnd from '../components/EditingRoom/DeadEnd';
import Receipt from '../components/EditingRoom/Receipt';
import ModelPicker from '../components/Common/ModelPicker';
import { usePaidConfirm } from '../components/Common/usePaidConfirm';
import { MODE_VERIFIED } from '../components/EditingRoom/modes';
import {
  getWriterModels, getCheckingStatus, getSceneContext, preflightScene, answerPreflightQuestion,
  getPriceEstimate, generateScene, getGenerationStatus, regenerateScene, editScene, recheckScene, listSceneReceipts,
  confirmFact, rejectFact,
} from '../api/editingRoom';
import { settingsApi } from '../api/settings';
import './EditingRoomPage.css';

const DOSSIER_TYPE = 'missing_dossier';

// Heartbeat poll while generate is in flight. 3s matches the TASK; 45s
// stall is just over four missed pulses (server pulses every 10s) so a
// healthy long writer call does not trip the copy, but a hung OpenRouter
// wait does.
const GENERATION_POLL_MS = 3000;
const GENERATION_STALL_MS = 45000;

/**
 * The Editing Room (Unit 4 build spec, all of C+D+E): a separate room
 * (design brief §1), not a Story Board panel — routed as a sibling to
 * ProtectedLayout, its own chrome (TopBar) end to end. Orchestrates the
 * journey: pre-flight → fare card → in progress → review room (or dead
 * end) → receipt.
 */
export default function EditingRoomPage() {
  const { sceneId } = useParams();
  const navigate = useNavigate();

  const [screen, setScreen] = useState('loading');
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [actionError, setActionError] = useState(null);

  const [sceneContext, setSceneContext] = useState(null);
  const [models, setModels] = useState([]);
  const [claims, setClaims] = useState({});
  const [preflight, setPreflight] = useState(null);
  const [overrideDossierReason, setOverrideDossierReason] = useState(null);

  const [selectedModel, setSelectedModel] = useState(null);
  const [mode, setMode] = useState(MODE_VERIFIED);
  const [estimatesByMode, setEstimatesByMode] = useState({});

  const [current, setCurrent] = useState(null); // last generate/regenerate/edit/recheck response
  const [draftText, setDraftText] = useState('');
  const [rulings, setRulings] = useState({});
  const [generationStalled, setGenerationStalled] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedDraft, setEditedDraft] = useState('');

  const [receiptRow, setReceiptRow] = useState(null);
  const [factActionBusyId, setFactActionBusyId] = useState(null);
  // A writer who already paid for a draft can always find it (punch-list
  // §2.2): true once this page resumed straight into the review room from
  // an existing current receipt, rather than reaching it via a fresh
  // generate call. Drives the "Draft fresh" affordance below.
  const [resumed, setResumed] = useState(false);

  const modelName = useMemo(() => models.find((m) => m.id === selectedModel)?.name, [models, selectedModel]);
  const estimate = estimatesByMode[mode]?.[selectedModel];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ctx, { models: writerModels }, { claims: claimLines }, pf, { receipts }, settingsResponse] = await Promise.all([
          getSceneContext(sceneId), getWriterModels(), getCheckingStatus(), preflightScene(sceneId),
          listSceneReceipts(sceneId), settingsApi.getSettings().catch(() => null),
        ]);
        if (cancelled) return;
        setSceneContext(ctx);
        setModels(writerModels);
        // Seed the fare card's own selector from the writer's saved
        // prose_scene_generation preference (spec v2 §4/§9C) — but only
        // when that preference is one of the fare card's own small,
        // cost-estimated writer-model set (services/prose_generation/
        // writer_models.py). That set is a separate, already-shipped
        // registry and doesn't (yet) include every checked-menu id (e.g.
        // the new anthropic/claude-sonnet-5 default) — falling back keeps
        // the fare card's price estimate / Draft button working instead of
        // silently landing on an id with no estimate.
        const settingsData = settingsResponse?.data || settingsResponse;
        const preferredModel = settingsData?.model_preferences?.prose_scene_generation;
        const preferredIsWriterModel = preferredModel && writerModels.some((m) => m.id === preferredModel);
        setSelectedModel((preferredIsWriterModel ? preferredModel : writerModels[0]?.id) || null);
        setClaims(claimLines);
        setPreflight(pf);

        const currentReceipt = receipts.find((r) => r.status === 'current');
        if (currentReceipt) {
          setReceiptRow(currentReceipt);
          setDraftText(currentReceipt.draft_text);
          setCurrent({
            status: 'complete',
            receipt_id: currentReceipt.id,
            receipt_version: currentReceipt.version,
            draft: currentReceipt.draft_text,
            structured_findings: currentReceipt.fixes?.structured_findings || [],
            structured_advisory_findings: currentReceipt.fixes?.structured_advisory_findings || [],
            harvested_facts: [],
            attempts: 1,
          });
          setRulings({});
          setResumed(true);
          setScreen('review');
        } else {
          setScreen(pf.cleared ? 'fare' : 'preflight');
        }
      } catch (err) {
        if (!cancelled) { setLoadError(err.message); setScreen('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [sceneId]);

  const dossierQuestions = preflight?.questions.filter((q) => q.type === DOSSIER_TYPE) || [];
  const otherQuestions = preflight?.questions.filter((q) => q.type !== DOSSIER_TYPE) || [];
  const canProceedPastPreflight = otherQuestions.length === 0 && (dossierQuestions.length === 0 || !!overrideDossierReason);

  useEffect(() => {
    if (screen === 'preflight' && canProceedPastPreflight) setScreen('fare');
  }, [screen, canProceedPastPreflight]);

  useEffect(() => {
    if (screen !== 'fare' || !sceneId || estimatesByMode[mode]) return;
    let cancelled = false;
    (async () => {
      try {
        const { models: priced } = await getPriceEstimate(sceneId, mode);
        if (cancelled) return;
        const byModel = {};
        priced.forEach((p) => { byModel[p.id] = p; });
        setEstimatesByMode((prev) => ({ ...prev, [mode]: byModel }));
      } catch (err) {
        if (!cancelled) setActionError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [screen, sceneId, mode, estimatesByMode]);

  const handleAnswer = useCallback(async (payload) => {
    setBusy(true);
    try {
      await answerPreflightQuestion(sceneId, payload);
      const pf = await preflightScene(sceneId);
      setPreflight(pf);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }, [sceneId]);

  const handleOverrideDossier = useCallback((reason) => setOverrideDossierReason(reason), []);
  const handleBackToOutline = useCallback(() => navigate(-1), [navigate]);
  const handleDraftFresh = useCallback(() => {
    setActionError(null);
    setScreen(preflight?.cleared ? 'fare' : 'preflight');
  }, [preflight]);

  const handleGenerateResult = useCallback((result) => {
    setCurrent(result);
    if (result.status === 'needs_decision') {
      setScreen('deadend');
      return;
    }
    setDraftText((prev) => result.draft ?? prev);
    setRulings({});
    setScreen('review');
  }, []);

  const runGenerate = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    setGenerationStalled(false);
    setScreen('progress');
    try {
      const result = await generateScene(sceneId, {
        model: selectedModel, mode, overrideDossierReason: overrideDossierReason || undefined,
      });
      if (result.draft !== undefined) setDraftText(result.draft);
      handleGenerateResult(result);
    } catch (err) {
      setActionError(err.message);
      setScreen('fare');
    } finally {
      setBusy(false);
    }
  }, [sceneId, selectedModel, mode, overrideDossierReason, handleGenerateResult]);

  useEffect(() => {
    if (screen !== 'progress' || !sceneId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await getGenerationStatus(sceneId);
        if (cancelled || !status?.last_progress_at) return;
        const age = Date.now() - Date.parse(status.last_progress_at);
        if (!Number.isNaN(age)) setGenerationStalled(age >= GENERATION_STALL_MS);
      } catch {
        // Advisory only — the generate POST is still the completion path.
      }
    };
    tick();
    const id = setInterval(tick, GENERATION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [screen, sceneId]);

  const fetchCurrentReceiptRow = useCallback(async (receiptId) => {
    const { receipts } = await listSceneReceipts(sceneId);
    return receipts.find((r) => r.id === receiptId) || receipts.find((r) => r.status === 'current') || receipts[0] || null;
  }, [sceneId]);

  const goToReceipt = useCallback(async (result) => {
    setBusy(true);
    try {
      const row = await fetchCurrentReceiptRow(result.receipt_id);
      setReceiptRow(row);
      setScreen('receipt');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }, [fetchCurrentReceiptRow]);

  const handleToggleEdit = useCallback(() => {
    if (!editing) { setEditedDraft(draftText); setEditing(true); return; }
    setBusy(true);
    editScene(sceneId, editedDraft)
      .then((result) => {
        setCurrent(result);
        setDraftText(result.draft);
        setRulings({});
        setEditing(false);
      })
      .catch((err) => setActionError(err.message))
      .finally(() => setBusy(false));
  }, [editing, draftText, editedDraft, sceneId]);

  const handleRecheckEdits = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      await editScene(sceneId, editedDraft);
      const result = await recheckScene(sceneId);
      setCurrent(result);
      setDraftText(editedDraft);
      setRulings({});
      setEditing(false);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }, [sceneId, editedDraft]);

  const handleRegenerate = useCallback(async (writerNote) => {
    const ok = await askPaid('editing_regenerate');
    if (!ok) return;
    const agreedFindingIds = Object.entries(rulings).filter(([, v]) => v === 'agree').map(([id]) => id);
    const overruledFindingIds = Object.entries(rulings).filter(([, v]) => v === 'overrule').map(([id]) => id);
    setBusy(true);
    setActionError(null);
    try {
      const result = await regenerateScene(sceneId, {
        agreedFindingIds, overruledFindingIds, model: selectedModel, writerNote,
      });
      setCurrent(result);
      setDraftText(result.draft ?? draftText);
      setRulings({});
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  }, [sceneId, rulings, selectedModel, draftText, askPaid]);

  const handleFactAction = useCallback(async (factId, action) => {
    setFactActionBusyId(factId);
    try {
      const fn = action === 'confirm' ? confirmFact : rejectFact;
      const updated = await fn(factId);
      setCurrent((prev) => prev && ({
        ...prev,
        harvested_facts: (prev.harvested_facts || []).map((f) => (
          f.fact_id === factId ? { ...f, state: updated.state || (action === 'confirm' ? 'confirmed' : 'rejected') } : f
        )),
      }));
    } catch (err) {
      setActionError(err.message);
    } finally {
      setFactActionBusyId(null);
    }
  }, []);

  if (screen === 'loading') {
    return (
      <div className="er-page">
        <TopBar storyName={sceneContext?.story_name} sceneTitle={sceneContext?.scene_title} />
        <div className="er-page__loading">Loading the scene…</div>
      </div>
    );
  }

  if (screen === 'error') {
    return (
      <div className="er-page">
        <TopBar />
        <div className="er-page__loading">Couldn&rsquo;t load this scene: {loadError}</div>
      </div>
    );
  }

  const planSummary = sceneContext ? {
    beatCount: sceneContext.beat_count, ruleCount: sceneContext.rule_count,
    factCount: sceneContext.fact_count, wordTarget: sceneContext.word_target,
  } : null;

  // needs_decision responses (pipeline.py's generate_scene dead-end branch)
  // nest these under `checks` instead of at the top level — the shape only
  // COMPLETE-status responses (generate/regenerate/edit/recheck) share.
  const structuredFindings = current?.structured_findings || current?.checks?.structured_findings || [];
  const structuredAdvisory = current?.structured_advisory_findings || current?.checks?.structured_advisory_findings || [];
  const overruledFindings = structuredFindings.filter((f) => rulings[f.id] === 'overrule');

  return (
    <div className="er-page">
      {paidDialog}
      <TopBar
        storyName={sceneContext?.story_name}
        sceneTitle={sceneContext?.scene_title}
        statusLabel={current?.unverified ? 'Sketch — unverified' : undefined}
      />

      {screen === 'preflight' && preflight && (
        <PreflightQA
          questions={preflight.questions}
          onAnswer={handleAnswer}
          onOverrideDossier={handleOverrideDossier}
          onBackToOutline={handleBackToOutline}
          busy={busy}
        />
      )}

      {screen === 'fare' && (
        <>
          {/* The writer's saved prose-generation preference (Settings → My
              Models), independent of the fare card's own per-scene "Which
              model writes" override below — spec v2 §4/§9C. */}
          <div className="er-page__model-pref">
            <span className="er-page__model-pref-label">Your default prose model</span>
            <ModelPicker mode="select" functionKey="prose_scene_generation" disabled={busy} />
          </div>
          <FareCard
            planSummary={planSummary}
            models={models}
            estimatesByModel={estimatesByMode[mode]}
            selectedModel={selectedModel}
            onSelectModel={setSelectedModel}
            mode={mode}
            onSelectMode={setMode}
            onDraft={runGenerate}
            busy={busy}
            error={actionError}
          />
        </>
      )}

      {screen === 'progress' && (
        <InProgress stalled={generationStalled} modelName={modelName} />
      )}

      {screen === 'review' && current?.unverified && (
        <div className="er-page__sketch">
          <p className="er-page__sketch-note">Sketch draft — unverified, no receipt.</p>
          <div className="er-page__sketch-prose">
            {draftText.split(/\n{2,}/).filter(Boolean).map((p, i) => <p key={i}>{p.trim()}</p>)}
          </div>
        </div>
      )}

      {screen === 'review' && current && !current.unverified && (
        <ReviewRoom
          draft={draftText}
          wordCount={draftText.split(/\s+/).filter(Boolean).length}
          modelName={modelName}
          generatedAt={null}
          findings={structuredFindings}
          advisoryFindings={structuredAdvisory}
          rulings={rulings}
          onRule={(id, ruling) => setRulings((prev) => ({ ...prev, [id]: ruling }))}
          editing={editing}
          onToggleEdit={handleToggleEdit}
          editedDraft={editedDraft}
          onDraftChange={setEditedDraft}
          onAcceptAsIs={() => goToReceipt(current)}
          onRecheckEdits={handleRecheckEdits}
          onRegenerate={handleRegenerate}
          regenerateEstimate={estimatesByMode[MODE_VERIFIED]?.[selectedModel]}
          busy={busy}
          onDraftFresh={resumed ? handleDraftFresh : undefined}
        />
      )}

      {screen === 'deadend' && current && (
        <DeadEnd
          bestDraft={draftText}
          findings={structuredFindings}
          advisoryFindings={structuredAdvisory}
          attempts={current.attempts}
          options={current.options}
          onAcceptAnyway={() => goToReceipt(current)}
          onTryAgain={runGenerate}
          tryAgainEstimate={estimatesByMode[mode]?.[selectedModel]}
          onGoToOutline={handleBackToOutline}
          busy={busy}
        />
      )}

      {screen === 'receipt' && (
        <Receipt
          claims={claims}
          findings={structuredFindings.filter((f) => rulings[f.id] !== 'overrule')}
          advisoryFindings={structuredAdvisory}
          overruledFindings={overruledFindings}
          beatAccount={receiptRow?.beat_account}
          harvestedFacts={current?.harvested_facts}
          modelName={modelName}
          estimate={estimate}
          actualCost={receiptRow?.fixes?.actual_cost}
          attempts={current?.attempts || 1}
          editHistoryNote={receiptRow?.beat_account?.status === 'edited_by_writer' ? 'Edited by writer after inspection.' : null}
          onConfirmFact={(id) => handleFactAction(id, 'confirm')}
          onRejectFact={(id) => handleFactAction(id, 'reject')}
          factActionBusyId={factActionBusyId}
          onDone={handleBackToOutline}
        />
      )}
    </div>
  );
}
